import { ReactNode } from "react";
import {
  DefaultValueExceptionType,
  KeyValue,
  LabelValue,
} from "../../../helpers/types/common";
import { AddDeliverableType } from "../../../helpers/constants";
import { Locale } from "../../../api/services/common/models";

export interface AccordionStatusProgramTemplate {
  profile: boolean;
  activity: boolean;
  all: boolean;
  compatibleTemplate: boolean;
  [key: string]: boolean;
}

export const ProgramTemplateFormKey = {
  programName: "programName",
  lineOfBusiness: "lineOfBusiness",
  programFrameWork: "programFrameWork",
  solutionCategory: "solutionCategory",
  solutionCategoryLob: "solutionCategoryLob",
  solutionOffering: "solutionOffering",
  programClass: "programClass",
  duration: "duration",
  status: "status",
  country: "country",
  programNameByLanguage: "programNameByLanguage",
  localizedProgramTemplateNames: "localizedProgramTemplateNames",
  activities: "activities",
  closingReasonType: "closingReasonType",
  nextAccess: "nextAccess",
  selectRegionType: "selectRegionType",
} as const;

export interface SolutionOffering {
  id: string;
  name: string;
  nameKey: string;
}

export interface LocalizedProgramTemplateNames {
  localekey: string;
  name: string;
}

export interface ProgramTemplateProfileFormProps {
  programName: string;
  lineOfBusiness: string;
  programFrameWork: string;
  solutionCategory: string;
  solutionCategoryLob: string;
  solutionOffering: string;
  programClass: string;
  duration: number;
  status: string;
  country: string[];
  programNameByLanguage: string;
  localizedProgramTemplateNames?: LocalizedProgramTemplateNames[];
  activities?: ProgramTemplateActivitiesRowValue[];
  closingReasonType: string;
  nextAccess: string;
  selectRegionType: string;
}

export const ProgramTemplateActivitiesFormKey = {
  programFramework: "programFramework",
  activityType: "activityType",
  modality: "modality",
  topic: "topic",
  activityClass: "activityClass",
  activityStatus: "activityStatus",
  units: "units",
  measurement: "measurement",
  candidateStatus: "candidateStatus",
  tableBody: "tableBody",
  tableRow: "tableRow",
  tableFooter: "tableFooter",
  tablePagination: "tablePagination",
} as const;

export const ProgramTemplateDeliverablesFormKey = {
  name: "name",
  deliverableType: "deliverableType",
  units: "units",
  measurement: "measurement",
  activityTypes: "activityTypes",
  tableBody: "tableBody",
  tableRow: "tableRow",
  tableFooter: "tableFooter",
  tablePagination: "tablePagination",
} as const;

export interface ProgramTemplateActivitiesRowValue {
  id: string;
  programFrameworkId: string | null;
  programFramework?: string;
  activityType: string;
  modality: string[];
  topic: string;
  activityClass: string;
  activityStatus: string;
  units: string;
  measurement: string;
  candidateStatus: string[];
  [key: string]: any;
}

export interface ProgramTemplateDeliverablesRowValue {
  id: string;
  name: string | null;
  deliverableType: string;
  units: string;
  measurement: string;
  activityTypes: ActivityType[];
  isFrameworkActivityDeliverable: boolean;
  [key: string]: any;
}

export interface ActivityType {
  id: string;
  name: string | null;
}

export interface UniqueFiltersTypes {
  modality: LabelValue[];
  programFramework: LabelValue[];
  units: LabelValue[];
}

export interface DeliverableUniqueFiltersTypes {
  activityTypes: LabelValue[];
  deliverableType: LabelValue[];
  units: LabelValue[];
}

export interface ProgramTemplateActivitiesAndDeliverablesProps {
  rows: ProgramTemplateActivitiesRowValue[];
  deliverableRows: ProgramTemplateDeliverablesRowValue[];
  onCreateActivityClick: () => void;
  successHandler: () => void;
  successHandlerDeliverables: () => void;
  programTemplateId: string | undefined;
  isViewTemplate?: boolean;
  onCreateDeliverableClick: () => void;
  frameWorkId?: string;
}

export interface ProgramTemplateActivitiesTabProps {
  rows: ProgramTemplateActivitiesRowValue[];
  tableRef: React.RefObject<any>;
  isViewTemplate: boolean;
  control: any;
  uniqueFilters: UniqueFiltersTypes;
  filteredResults: ProgramTemplateActivitiesRowValue[];
  deleteAction: (id: string) => void;
  onActivityViewClick: (
    data: ProgramTemplateActivitiesRowValue,
    isEdit?: boolean,
  ) => void;
}

export interface ProgramTemplateDeliverablesTabProps {
  rows: ProgramTemplateDeliverablesRowValue[];
  tableRef: React.RefObject<any>;
  isViewTemplate: boolean;
  control: any;
  uniqueFilters: DeliverableUniqueFiltersTypes;
  filteredResults: ProgramTemplateDeliverablesRowValue[];
  deleteAction: (id: string) => void;
  onDeliverableViewClick: (
    data: ProgramTemplateDeliverablesRowValue,
    isEdit?: boolean,
  ) => void;
}

export interface ManageLocales {
  isMandatory: boolean;
  key: string;
  isEnglish: boolean;
}

export interface NameMandatoryByLanguage {
  key?: string;
  placeholderKey?: string;
}

export const CreateActivityGeneralFormKey = {
  candidateStatus: "candidateStatus",
  activityType: "activityType",
  activityClass: "activityClass",
  modality: "modality",
  topic: "topic",
  deliverables: "deliverables",
  activityStatus: "activityStatus",
  triggerBilling: "triggerBilling",
  lastContactDate: "lastContactDate",
  triggerTouch: "triggerTouch",
} as const;

export interface CreateActivityGeneralProps {
  candidateStatus: string[];
  activityType: string;
  modality: string[];
  topic: KeyValue;
  deliverables: string[];
  activityStatus: string;
  triggerBilling: boolean;
  lastContactDate: boolean;
  triggerTouch: boolean;
}

export interface ActivityDeliverableType {
  id: string;
  name: string;
}

export const CreateActivityDefaultFormKey = {
  units: "units",
  measurementMinute: "measurementMinute",
  measurementOther: "measurementOther",
  country: "country",
  countryMeasurementMinute: "countryMeasurementMinute",
  countryMeasurementOther: "countryMeasurementOther",
  measurement: "measurement",
  measurementName: "measurementName",
} as const;

export interface CreateActivityDefaultProps {
  units: string;
  measurementMinute: string;
  measurementOther: number | null;
  country?: string;
  countryMeasurementMinute: string;
  countryMeasurementOther: number;
}

export interface EditActivityValueProps
  extends CreateActivityGeneralProps,
    CreateActivityDefaultProps {
  defaultValueExceptions: DefaultValueExceptionType[];
  activityName: string;
}

export interface CreateDeliverablesFormProps {
  addDeliverableType: AddDeliverableType;
  deliverableType: string;
  name: string;
  units: string;
  measurementMinute: string;
  measurementOther: number | null;
}

export const CreateDeliverablesFormKey = {
  addDeliverableType: "addDeliverableType",
  deliverableType: "deliverableType",
  name: "name",
  units: "units",
  measurementMinute: "measurementMinute",
  measurementOther: "measurementOther",
} as const;

export interface ExistingDeliverablesType {
  deliverableType: string;
  units: string;
  measurement: number;
  isSelected: boolean;
  id: string;
  name: string;
  unitName: string;
  deliverableTypeName: string;
  [key: string]: any;
}

export const ExistingDeliverableFormKey = {
  deliverableType: "deliverableType",
  units: "units",
  measurement: "measurement",
  isSelected: "isSelected",
  id: "id",
  name: "name",
  unitName: "unitName",
  deliverableTypeName: "deliverableTypeName",
} as const;

export interface ExistingDeliverableFilterFormProps {
  deliverableType: string;
  name: string;
  units: string;
}

export interface UpdateDeliverablesFormProps {
  units: string;
  measurementMinute: string;
  measurementOther: number | null;
}

export const UpdateDeliverablesFormKey = {
  units: "units",
  measurementMinute: "measurementMinute",
  measurementOther: "measurementOther",
} as const;

export const ProgramTemplateAddCompatibleFormKey = {
  country: "country",
  compatibleProgramTemplate: "compatibleProgramTemplate",
  duration: "duration",
} as const;

export interface CompatibleProgramTemplate {
  id: string;
  country: string;
  compatibleProgramTemplate: string;
  duration: string;
}

export const CompatibleProgramTemplateResultFormKey = {
  countryName: "countryName",
  compatibleProgramTemplateName: "compatibleProgramTemplateName",
  duration: "duration",
} as const;

export interface CompatibleResult {
  id: string;
  countryCode: string;
  countryName: string;
  compatibleProgramTemplateId: string;
  compatibleProgramTemplateName: string;
  duration: number;
  [key: string]: string | number | ReactNode;
}
export interface CompatibleTemplateByCountry {
  id: string;
  name: string;
  durationInMonths: number;
}

export interface TemplateActivityData {
  localizedTopics?: Locale[];
  topic?: string;
}
